/**
 * Modal "Changer mon mot de passe" — self-service pour tous les utilisateurs.
 *
 * Endpoint: POST /api/auth/change-password
 * Payload: { current_password, new_password }
 *
 * Pattern modal cohérent : header sticky + body scrollable + footer sticky.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Lock, Eye, EyeOff, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => {
  const t = localStorage.getItem('dermascan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const ChangePasswordDialog = ({ onClose, onSuccess }) => {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const valid = current.length >= 1 && next.length >= 6 && next === confirm;
  const newTooShort = next.length > 0 && next.length < 6;
  const mismatch = confirm.length > 0 && next !== confirm;

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: current, new_password: next },
        { headers: auth() }
      );
      toast.success('Mot de passe mis à jour');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du changement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 pb-[calc(env(safe-area-inset-bottom)+80px)] sm:pb-3"
      onClick={onClose}
      data-testid="change-password-dialog"
    >
      <div
        className="bg-white rounded-xl max-w-md w-full max-h-[85vh] flex flex-col overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 bg-gradient-to-r from-[#1B4D3E] to-[#2E7D32] px-5 py-3 flex items-center justify-between text-white">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            <h2 className="font-semibold">Changer mon mot de passe</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10" data-testid="change-password-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0 p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-zinc-700 mb-1 block">Mot de passe actuel</label>
            <div className="relative">
              <Input
                type={showCurrent ? 'text' : 'password'}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                autoFocus
                placeholder="Votre mot de passe actuel"
                className="pr-10"
                data-testid="cp-current"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
              >
                {showCurrent ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-zinc-700 mb-1 block">Nouveau mot de passe</label>
            <div className="relative">
              <Input
                type={showNext ? 'text' : 'password'}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="6 caractères minimum"
                className="pr-10"
                data-testid="cp-new"
              />
              <button
                type="button"
                onClick={() => setShowNext(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
              >
                {showNext ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {newTooShort && (
              <p className="text-xs text-amber-700 mt-1">Le nouveau mot de passe doit contenir au moins 6 caractères.</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-zinc-700 mb-1 block">Confirmer le nouveau</label>
            <Input
              type={showNext ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Re-saisir le nouveau mot de passe"
              data-testid="cp-confirm"
            />
            {mismatch && (
              <p className="text-xs text-red-700 mt-1">Les deux mots de passe ne correspondent pas.</p>
            )}
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5 text-xs text-emerald-900">
            <b>💡 Conseil :</b> utilisez un mot de passe que vous seul connaissez.
            Ne le partagez avec personne, même pas vos collègues.
          </div>
        </div>

        <div className="shrink-0 border-t border-zinc-200 bg-white px-5 py-3 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={!valid || submitting}
            className="bg-[#1B4D3E] hover:bg-[#1B4D3E]/90 text-white"
            data-testid="cp-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordDialog;
